import {
	EnvironmentContextProvider,
	config,
	createCollateralLibraryRoutes,
	createResourceRoutes,
	createLegalRoutes,
} from '@ds-mgnl/marketing';
import type { LinkSourceProps, LinkSourceRecord } from '@dsx/react';
import { Provider as DsxProvider } from '@dsx/react';
import { forwardRef } from 'react';
import {
	Outlet,
	Link as RouterLink,
	RouterProvider,
	createBrowserRouter,
} from 'react-router-dom';
import styles from './App.module.css';
import './assets/pdf/17877-TELS-sp-tech-overview-sheet-LoResFin.pdf';
import './assets/pdf/TBS-Interior-Service-Provider-Expectations.pdf';
import './assets/robots/robots.txt';
import { FooterTels } from './components/FooterTels';
import { TopNavTels } from './components/TopNavTels';
import { useScrollManagement } from './hooks/useScrollManagement';
import { useUtmFormTracking } from './hooks/UseUtmFormTracking';
import { ErrorPage } from './pages/ErrorPage';
import { MagnoliaPage, magnoliaPageLoader } from './pages/MagnoliaPage';
import { getMagnoliaBaseDamUrl, getMagnoliaBaseUrl } from './util';

const linkSources = {
	default: forwardRef<HTMLAnchorElement, LinkSourceProps>((props, ref) => (
		<RouterLink ref={ref} {...props} to={props.href || ''} />
	)),
	external: forwardRef<HTMLAnchorElement, LinkSourceProps>((props, ref) => (
		<a ref={ref} {...props} />
	)),
	none: forwardRef<HTMLSpanElement, LinkSourceProps>((props, ref) => (
		<span ref={ref} {...props} />
	)),
} as LinkSourceRecord;

const router = createBrowserRouter([
	{
		element: <RootLayout />,
		children: [
			...createResourceRoutes({
				errorElement: <ErrorPage />,
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				resourcesHomeUrlPath: '/resources',
				pageConfig: config,
				showOnSite: 'tels',
			}),
			...createLegalRoutes({
				errorElement: <ErrorPage />,
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				legalHomeUrlPath: '/legal',
				pageConfig: config,
				showOnSite: 'tels',
			}),
			...createCollateralLibraryRoutes({
				errorElement: <ErrorPage />,
				collateralLibUrlPath: '/collateral-library',
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				mgnlBaseDamUrl: getMagnoliaBaseDamUrl() ?? '',
				mgnlCollateralHomePath: '/collateral-library',
				siteTitleSuffix: 'Direct Supply TELS',
				showOnSite: 'tels',
			}),
			{
				path: '*',
				element: <MagnoliaPage />,
				loader: magnoliaPageLoader,
				errorElement: <ErrorPage />,
			},
		],
	},
]);

export function App() {
	useUtmFormTracking();
	return (
		<DsxProvider locale="en-US" linkSources={linkSources}>
			<EnvironmentContextProvider
				value={{
					damHost: getMagnoliaBaseDamUrl() ?? '',
					appHost: getMagnoliaBaseUrl() ?? '',
					isInMagnolia: false,
				}}
			>
				<RouterProvider router={router} />
			</EnvironmentContextProvider>
		</DsxProvider>
	);
}

function RootLayout() {
	useScrollManagement();
	return (
		<div className={styles['site-container']}>
			<TopNavTels />
			<main>
				<Outlet />
			</main>
			<FooterTels />
		</div>
	);
}
